"use client"

import { useCallback, useMemo, useRef, useState } from "react"
import { TextInput, View } from "react-native"
import { FormattedMessage, useIntl } from "react-intl"

import {
  Box,
  Button,
  Dropdown,
  getCategoryEmoji,
  Icon,
  P,
  PressableBox,
  ScrollBox,
  useCategories,
} from "@bullseye/components"

import DiscoveryBadge from "./DiscoveryBadge"
import DiscoveryTooltip from "./DiscoveryTooltip"

type Props = {
  value: string[]
  onChange: (categoryIds: string[]) => void
  isNew?: boolean
  onDiscoverySeen?: () => void
  anchorRef?: React.RefObject<View>
  accessToken?: string
}

export const CategoryFilter = ({
  value,
  onChange,
  isNew,
  onDiscoverySeen,
  anchorRef: externalAnchorRef,
  accessToken,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const [draft, setDraft] = useState<string[]>(value)
  const [search, setSearch] = useState("")
  const [tooltipDismissed, setTooltipDismissed] = useState(false)
  const internalAnchorRef = useRef<View>(null)
  const anchorViewRef = externalAnchorRef ?? internalAnchorRef
  const renderTooltipInternally = !externalAnchorRef
  const { categories } = useCategories({ accessToken })
  const intl = useIntl()

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.title.localeCompare(b.title)),
    [categories],
  )

  const filteredCategories = useMemo(
    () =>
      sortedCategories.filter((c) =>
        c.title.toLowerCase().includes(search.toLowerCase()),
      ),
    [sortedCategories, search],
  )

  const handleOpen = useCallback(() => {
    setDraft(value)
    setSearch("")
    setIsOpen(true)
    onDiscoverySeen?.()
    setTooltipDismissed(true)
  }, [value, onDiscoverySeen])

  const handleToggle = useCallback((id: string) => {
    setDraft((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id],
    )
  }, [])

  const handleApply = useCallback(() => {
    onChange(draft)
    setIsOpen(false)
  }, [draft, onChange])

  const handleClear = useCallback(() => {
    setDraft([])
  }, [])

  const handleDismiss = useCallback(() => {
    setSearch("")
  }, [])

  const handleTooltipCta = useCallback(() => {
    onDiscoverySeen?.()
    setTooltipDismissed(true)
    handleOpen()
  }, [onDiscoverySeen, handleOpen])

  const hasSelection = value.length > 0
  const showTooltip = !!isNew && !tooltipDismissed && !isOpen

  const label = hasSelection
    ? intl.formatMessage(
        { id: "filters.category.labelWithCount" },
        { count: value.length },
      )
    : intl.formatMessage({ id: "filters.category.label" })

  const filterButton = (
    <Button
      as="button"
      startIconType="plus"
      endIconType="chevron-down"
      handlePress={handleOpen}
      rounded
      outlined
      variant="secondary"
      active={isOpen || hasSelection}
      compact
    >
      {label}
    </Button>
  )

  return (
    <Box className="relative">
      <Dropdown
        id="explore-category-filter"
        open={isOpen}
        setOpen={setIsOpen}
        sheetSize="md"
        onDismiss={handleDismiss}
        toggleComponent={
          <View ref={anchorViewRef} collapsable={false}>
            {isNew ? (
              <DiscoveryBadge
                label={label}
                onPress={handleOpen}
                active={isOpen || hasSelection}
              >
                {filterButton}
              </DiscoveryBadge>
            ) : (
              filterButton
            )}
          </View>
        }
        title={<FormattedMessage id="filters.category.label" />}
      >
      <Box className="flex g-4 px-4">
        <Box className="flex g-1">
          <P bold className="text-lg">
            <FormattedMessage id="filters.category.label" />
          </P>
          <P className="text-gray-500">
            <FormattedMessage id="filters.category.subtitle" />
          </P>
        </Box>

        <Box
          className="flex flex-row items-center rounded-lg px-3"
          style={{ backgroundColor: "#eeedf0", height: 40 }}
        >
          <Icon type="magnifying-glass" size="sm" color="text-gray-500" />
          <TextInput
            value={search}
            onChangeText={setSearch}
            placeholder={intl.formatMessage({
              id: "filters.category.searchPlaceholder",
            })}
            accessibilityLabel={intl.formatMessage({
              id: "filters.category.searchPlaceholder",
            })}
            placeholderTextColor="#625a6d"
            style={{
              flex: 1,
              marginLeft: 6,
              fontSize: 16,
              color: "#26222f",
              // @ts-expect-error outline is a web-only CSS property
              outline: "none",
            }}
          />
        </Box>

        <ScrollBox
          className="min-h-[250px] max-h-[250px]"
          contentContainerStyle={{ gap: 10 }}
        >
          {filteredCategories.map((category) => {
            const isChecked = draft.includes(category.id)
            return (
              <PressableBox
                key={category.id}
                onPress={() => handleToggle(category.id)}
                className={`flex flex-row items-center self-start rounded-full border-solid px-4 py-2 g-2 ${isChecked ? "border-2 border-primary-500 bg-primary-50" : "border border-primary-200 bg-white"}`}
              >
                {getCategoryEmoji(category.title) !== "" && (
                  <P>{getCategoryEmoji(category.title)}</P>
                )}
                <P>{category.title}</P>
              </PressableBox>
            )
          })}
        </ScrollBox>

        <Box className="flex flex-row g-2">
          <Button
            as="button"
            handlePress={handleClear}
            variant="tertiary"
            className="flex-1"
            disabled={draft.length === 0}
          >
            <FormattedMessage id="filters.category.clear" />
          </Button>
          <Button
            as="button"
            handlePress={handleApply}
            className="flex-1"
          >
            <FormattedMessage id="filters.category.apply" />
          </Button>
        </Box>
      </Box>
      </Dropdown>


      {renderTooltipInternally && (
        <DiscoveryTooltip
          visible={showTooltip}
          anchorRef={anchorViewRef}
          onCtaPress={handleTooltipCta}
        />
      )}
    </Box>
  )
}
